import { useState } from 'react';
import { Banner, Link } from '@tackle-io/platform-ui';
import { Grid, Typography, Box, makeStyles } from 'vendor/material';
import { Link as MUILink } from 'vendor/material';
import { Cloud } from 'utils/cloudTypes';
import useVendorMarketplaceStatus, {
  VendorMarketplaceConnection,
} from 'hooks/useVendorMarketplaceStatus';
import {
  ONBOARDING_AWS_DOCS_URL,
  ONBOARDING_GCP_DOCS_URL,
  ONBOARDING_MSFT_DOCS_URL,
} from 'utils/constants';

const useStyles = makeStyles((theme) => ({
  errorMessage: {
    fontSize: theme.typography.pxToRem(14),
    paddingLeft: theme.spacing(1),
    color: theme.palette.NEUTRAL800,
    '& > a': {
      textDecoration: 'none',
    },
  },
  accordingLink: {
    color: theme.palette.BLUE400,
    paddingLeft: theme.spacing(1),
  },
  statusTitle: {
    fontWeight: 'bold',
    paddingBottom: theme.spacing(1),
  },
  errorList: {
    marginTop: theme.spacing(1),
  },
  link: {
    color: theme.palette.BLUE400,
  },
}));

const DOCUMENTATION_URL_BY_CLOUD = {
  [Cloud.Aws]: ONBOARDING_AWS_DOCS_URL,
  [Cloud.Gcp]: ONBOARDING_GCP_DOCS_URL,
  [Cloud.Azure]: ONBOARDING_MSFT_DOCS_URL,
} as const;
const TITLE_BY_CLOUD = {
  [Cloud.Aws]: 'AWS Marketplace',
  [Cloud.Azure]: 'Microsoft commercial marketplace',
  [Cloud.Gcp]: 'Google Cloud Marketplace',
} as const;

const VendorStatusBanner = ({
  cloud,
}: {
  cloud: Cloud.Aws | Cloud.Azure | Cloud.Gcp;
}) => {
  const classes = useStyles();
  const [isErrorReportOpen, setErrorReportOpen] = useState<boolean>(false);
  const marketplaceStatusData = useVendorMarketplaceStatus();

  const isDisconnected =
    cloud === Cloud.Aws
      ? marketplaceStatusData?.isAwsDisconnected
      : cloud === Cloud.Gcp
      ? marketplaceStatusData?.isGcpDisconnected
      : cloud === Cloud.Azure
      ? marketplaceStatusData?.isAzureDisconnected
      : false;

  if (!isDisconnected) return null;

  return (
    <Box pb={2}>
      <Banner
        title={`Data reporting for the ${TITLE_BY_CLOUD[cloud]} is disconnected`}
        type="danger"
        isCollapsible
        body={
          <Grid container spacing={2}>
            <Box>
              <Typography component="p" className={classes.errorMessage}>
                To connect, go to the step that requires attention and complete
                it.{' '}
                <Link to={DOCUMENTATION_URL_BY_CLOUD[cloud]} external>
                  Follow this guide
                </Link>{' '}
                or{' '}
                {/* <Link
                  to="https://scheduler.zoom.us/d/_akwuzhz/support-team-assistance"
                  external
                  className={classes.link}
                >
                  schedule a call
                </Link>{' '}
                with support. */}
                reach out to{' '}
                <Link to="mailto:support@tackle.io" external>
                  support@tackle.io
                </Link>
                .
              </Typography>
            </Box>
            <Box pt={2}>
              <MUILink
                component="button"
                variant="body2"
                className={classes.accordingLink}
                onClick={() => setErrorReportOpen(!isErrorReportOpen)}
              >
                {isErrorReportOpen ? 'Hide' : 'See'} detailed error report
              </MUILink>
              {isErrorReportOpen && (
                <Box pb={2}>
                  {cloud === Cloud.Aws && (
                    <>
                      <VendorStatus
                        title="AWS Role"
                        status={marketplaceStatusData?.aws?.tackleIam}
                      />
                      <VendorStatus
                        title="SDDS"
                        status={marketplaceStatusData?.aws?.sdds}
                      />
                      <VendorStatus
                        title="Event SQS"
                        status={marketplaceStatusData?.aws?.sqs}
                      />
                    </>
                  )}
                  {cloud === Cloud.Gcp && (
                    <VendorStatus
                      title=""
                      status={marketplaceStatusData?.gcp}
                    />
                  )}
                  {cloud === Cloud.Azure && (
                    <VendorStatus
                      title=""
                      status={marketplaceStatusData?.azure}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        }
      />
    </Box>
  );
};

const VendorStatus = ({
  title,
  status,
}: {
  title: string;
  status: VendorMarketplaceConnection;
}) => {
  const classes = useStyles();
  if (status?.status !== 'disconnected') return null;
  return (
    <Box pt={2} pl={1}>
      <Typography className={classes.statusTitle}>{title}</Typography>
      <Box pl={1}>
        <Typography>
          Status: <i>disconnected</i>
        </Typography>
        <Typography>
          Last connected at: <i>{status?.lastConnectedAt || 'Unknown'}</i>
        </Typography>
        <Typography>
          Updated at: <i>{status?.updatedAt || 'Unknown'}</i>
        </Typography>
        {status?.errors.length > 0 && (
          <Typography component="span">
            Errors:{' '}
            <ul className={classes.errorList}>
              {status?.errors.map((err) => (
                <li key={err}>
                  <i>{err}</i>
                </li>
              ))}
            </ul>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default VendorStatusBanner;
